import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { initialize, change, SubmissionError, destroy } from 'redux-form';
import { push } from 'react-router-redux';
import { get } from 'lodash';

import { LoadingBase } from 'Components';
import { Main, Filter } from 'Containers/';
import {
  EDIT_INGREDIENT_FORM_NAME,
  ING_PURCHASING_FORM_NAME,
  ING_MEASUREMENTS_FORM_NAME,
  EDIT_RECIPE_FORM_NAME,
} from 'Common/FormEnum';
import { getIsDirty } from 'Common/Selectors/DirtySelector';
import { errorToast, createToast, toastType } from 'Common/ToastHelper';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import {
  IngredientFieldEnum as Fields,
  EditIngredientFieldEnum as EditIngredientFields,
  AttributeFieldEnum as AttributeFields,
  IngMeasurementsFieldEnum as IngMeasurementFields,
  RecipeFieldEnum as RecipeFields,
} from 'Common/FieldEnum';
import MPWWebApi from 'API/MPWApi';
import { WEIGHT_UNITS } from 'Pages/manageingredient/helpers/Constants';
import { RecipeModal } from 'Pages/managemenu/subpages/editmenu/components/RecipeModal';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { isMeasurementComplete } from 'Pages/manageingredient/helpers/ingredient';
import { getProductLicenses } from 'State/product/actions';
import {
  getIsProductLicensed,
  getProductLicensesFetching,
} from 'State/product/selectors';
import { ProductEnum } from 'Common/Constants/Enumeration/ProductEnum';

import { CreateEditDupOrderEnum } from 'Common/Constants/Enumeration/ManageIngredientEnum';

import * as Selectors from '../../state/editingredient/selectors';
import { EditIngredientForm } from './EditIngredientForm';
import { MeasurementsAndPurchasingModal } from './components/MeasurementsAndPurchasingModal';
import {
  getIngredient,
  saveIngredient,
  clearErrors,
  setSaveSuccessful,
  getIngredientSources,
  getIngredientGroups,
  clearIngredientGroups,
  convertIngredientToRecipe,
  setConvertToRecipeSuccessful,
} from '../../state/editingredient/actions';

class EditIngredient extends Component {
  state = {
    ready: false,
    showMeasuresAndPurchasesModal: false,
    showRecipeModal: false,
  };

  get initialValues() {
    return {
      [Fields.Allergens]: [],
      [Fields.AllAllergens]: [],
    };
  }

  async componentDidMount() {
    const {
      formValues,
      getIngredient,
      getIngredientSources,
      getIngredientGroups,
      initialize,
      match: {
        params: { isNewEditDup, id },
      },
    } = this.props;

    getIngredientGroups();
    await getIngredientSources();

    const isDuplicate = isNewEditDup === CreateEditDupOrderEnum.DUP;

    const ingredientId = formValues[Fields.Id];
    if (ingredientId) {
      await getIngredient(ingredientId);
      await this.props.getProductLicenses();
    } else if (id) {
      await getIngredient(id, isDuplicate);
      await this.props.getProductLicenses();
    } else {
      try {
        const initialValues = { ...this.initialValues };
        const allergenResponse = await MPWWebApi.getGeneric(`Allergen`);
        const attributeResponse = await MPWWebApi.getGeneric(`Attribute`);
        initialValues[Fields.AllAllergens] = allergenResponse.data.Data;
        initialValues[AttributeFields.AllAttributes] =
          attributeResponse.data.Data;
        initialize(EDIT_INGREDIENT_FORM_NAME, initialValues);
      } catch (e) {
        createToast(
          toastType.error,
          'Error',
          'Could not retrieve allergens or attributes.',
          true
        );
      }
    }

    this.setState({ ready: true });
  }

  componentDidUpdate(prevProps) {
    const { saveSuccessful: prevSaveSuccessful } = prevProps;
    const {
      clearErrors,
      setSaveSuccessful,
      saveSuccessful,
      push,
      convertSuccessful,
      setConvertToRecipeSuccessful,
    } = this.props;

    if (errorToast(this.props, prevProps, 'fetchError')) {
      clearErrors();
    }

    if (prevSaveSuccessful !== saveSuccessful && saveSuccessful) {
      createToast(toastType.success, 'Success', 'Saved Successfully.', true);

      if (this.isNew) {
        push(`edit/${saveSuccessful}`);
      }
      setSaveSuccessful(null);
    }

    if (convertSuccessful) {
      this.setState({ showRecipeModal: true });
      setConvertToRecipeSuccessful(null);
    }
  }

  componentWillUnmount() {
    const { clearIngredientGroups, initialize, destroy } = this.props;

    clearIngredientGroups();
    initialize(ING_MEASUREMENTS_FORM_NAME, {
      [IngMeasurementFields.Measures]: [
        {
          [IngMeasurementFields.Amount]: '',
          [IngMeasurementFields.Measurement]: '',
          [IngMeasurementFields.Weight]: '',
          [IngMeasurementFields.BaseMeasure]: WEIGHT_UNITS.GRAMS,
          [IngMeasurementFields.IsDefault]: true,
        },
      ],
      [IngMeasurementFields.CostPerGram]: 0,
    });
    initialize(ING_PURCHASING_FORM_NAME, {});
    destroy(EDIT_INGREDIENT_FORM_NAME);
  }

  getMainProps = () => {
    const {
      openedInModal,
      isReadOnly,
      isSourceReadOnly,
      isUSDAIngredient,
      isSubRecipe,
    } = this.props;

    return {
      toolbox: {
        formName: EDIT_INGREDIENT_FORM_NAME,
        shown: true,
        backButtonProps: {
          shown: !openedInModal,
          path: '/manageingredient',
          callback: this.onBackClick,
        },
        saveButtonProps: {
          content: 'Save',
          disabled:
            isReadOnly ||
            (isSourceReadOnly && !isUSDAIngredient && !isSubRecipe),
        },
      },
      shouldNotUsePageWidth: true,
    };
  };

  saveIngredient = (formValues) => {
    const {
      saveIngredient,
      measuresAndPurchasingFormValues,
      purchasingFormValuesUsedForSaving,
    } = this.props;

    let data = {};

    const measures =
      measuresAndPurchasingFormValues[IngMeasurementFields.Measures];

    const hasAtLeast1Measure =
      Array.isArray(measures) && measures.some(isMeasurementComplete);

    if (!hasAtLeast1Measure) {
      createToast(
        toastType.error,
        'Error',
        'At least one ingredient measurement must be added.',
        true
      );
      throw new SubmissionError(
        'At least one ingredient measurement must be added.'
      );
    }

    data = {
      ...formValues,
      [EditIngredientFields.MeasuresAndCost]: {
        ...measuresAndPurchasingFormValues,
        ...purchasingFormValuesUsedForSaving,
      },
    };
    saveIngredient(data);
  };

  convertIngredientToRecipe = () => {
    const { formValues, setNotificationDialog } = this.props;
    if (this.isNew) {
      createToast(
        toastType.error,
        'Error',
        'You must create your ingredient first.',
        true
      );
      return;
    }
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.CONVERT_INGREDIENT,
      actionsToDispatchOnOk: [convertIngredientToRecipe(formValues)],
    });
  };

  toggleMeasuresAndPurchasesModal = (show) => {
    this.setState({ showMeasuresAndPurchasesModal: show });
  };

  openRecipeModal = (recipeId) => {
    const { change } = this.props;
    change(EDIT_RECIPE_FORM_NAME, RecipeFields.Id, recipeId);
    this.setState({ showRecipeModal: true });
  };

  closeRecipeModal = () => {
    this.setState({ showRecipeModal: false });
  };

  get isCreatedFromRecipe() {
    const { initialValues } = this.props;
    const createdFromRecipeId = initialValues[Fields.CreatedFromRecipeId];
    return !!createdFromRecipeId && !!createdFromRecipeId.length;
  }

  render() {
    const {
      fetching,
      formValues,
      ingredientSourcesOptions,
      ingredientGroupsOptions,
      ingredientSourcesMap,
      fetchingGroups,
      fetchingSources,
      initialValues,
      isReadOnly,
      isSourceReadOnly,
      isInventoryLicensed,
      match: {
        params: { isNewEditDup },
      },
    } = this.props;
    const {
      ready,
      showMeasuresAndPurchasesModal,
      showRecipeModal,
    } = this.state;

    if (fetching || !ready) {
      return <LoadingBase />;
    }

    const isDuplicate = isNewEditDup === CreateEditDupOrderEnum.DUP;

    const isSubRecipe = !!get(formValues, Fields.CreatedFromRecipeId, '');

    return (
      <Fragment>
        <Filter title="Create / Edit Ingredient" />
        <Main {...this.getMainProps()}>
          <EditIngredientForm
            ingredientSourcesOptions={ingredientSourcesOptions}
            ingredientGroupsOptions={ingredientGroupsOptions}
            ingredientSourcesMap={ingredientSourcesMap}
            formValues={formValues}
            onSubmit={this.saveIngredient}
            fetchingGroups={fetchingGroups}
            fetchingSources={fetchingSources}
            toggleMeasuresAndPurchasesModal={
              this.toggleMeasuresAndPurchasesModal
            }
            initialFormValues={initialValues}
            convertIngredientToRecipe={this.convertIngredientToRecipe}
            openRecipeModal={this.openRecipeModal}
            isSubRecipe={isSubRecipe}
            isDuplicate={isDuplicate}
          />
        </Main>
        <MeasurementsAndPurchasingModal
          isReadOnly={isReadOnly || isSubRecipe || isSourceReadOnly}
          isCreatedFromRecipe={this.isCreatedFromRecipe}
          modalProps={{
            open: showMeasuresAndPurchasesModal,
            onClose: () => this.toggleMeasuresAndPurchasesModal(false),
            closeOnDimmerClick: false,
            closeOnEscape: false,
            size: 'large',
          }}
          ingredientId={formValues[EditIngredientFields.Id]}
          isInventoryLicensed={isInventoryLicensed}
        />
        <RecipeModal
          modalProps={{
            size: 'fullscreen',
            open: showRecipeModal,
            onClose: this.closeRecipeModal,
            closeOnDimmerClick: false,
            closeOnEscape: false,
          }}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  if (ownProps.setPageLevelDirty) {
    ownProps.setPageLevelDirty(getIsDirty()(state));
  }

  const isSourceReadOnly = Selectors.getIsSourceReadOnly(state);
  const ingredientSourcesOptions = isSourceReadOnly
    ? Selectors.getIngredientSourcesOptions(state)
    : Selectors.getSaveableIngredientSourcesOptions(state);

  return {
    formValues: Selectors.getEditIngredientFormValues(state) || {},
    initialValues: Selectors.getEditIngredientInitialFormValues(state) || {},
    fetchError: Selectors.getFetchError(state),
    saveSuccessful: Selectors.getSaveSuccessful(state),
    fetching: Selectors.getFetching(state) || getProductLicensesFetching(state),
    ingredientGroupsOptions: Selectors.getIngredientGroupsOptions(state),
    ingredientSourcesMap: Selectors.getIngredientSourcesMap(state),
    ingredientSourcesOptions,
    fetchingGroups: Selectors.getFetchingGroups(state),
    fetchingSources: Selectors.getFetchingSources(state),
    measuresAndPurchasingFormValues: Selectors.getIngredientMeasuresAndCostFormValues(
      state
    ),
    purchasingFormValuesUsedForSaving: Selectors.getPurchasingFormValuesForSaving(
      state
    ),
    isReadOnly: Selectors.getIsReadOnly(state),
    isSourceReadOnly,
    isUSDAIngredient: Selectors.getIsUSDAIngredient(state),
    convertSuccessful: Selectors.getConvertSuccessful(state),
    isInventoryLicensed: getIsProductLicensed(state, ProductEnum.Inventory),
  };
};

const mapDispatchToProps = {
  setNotificationDialog,
  getIngredient,
  saveIngredient,
  clearErrors,
  initialize,
  setSaveSuccessful,
  getIngredientGroups,
  getIngredientSources,
  clearIngredientGroups,
  push,
  setConvertToRecipeSuccessful,
  change,
  destroy,
  getProductLicenses,
  getProductLicensesFetching,
};

EditIngredient = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditIngredient)
);

// See https://reactjs.org/docs/higher-order-components.html#static-methods-must-be-copied-over
// for why this is not part of the component
EditIngredient.path = '/manageingredient/edit/:isNewEditDup?/:id?';

export { EditIngredient };



// WEBPACK FOOTER //
// ./src/pages/manageingredient/subpages/editingredient/EditIngredient.js